import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Grid,
  Button,
  Stack
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import InPageNavSidebar, { InPageSection } from '../components/common/InPageNavSidebar';

const PrivacyPolicy = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Ensure the page starts at the top when navigated to
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const sections: InPageSection[] = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'information-collection', label: 'Information We Collect' },
    { id: 'information-use', label: 'How We Use Information' },
    { id: 'information-sharing', label: 'Information Sharing' },
    { id: 'data-security', label: 'Data Security' },
    { id: 'cookies', label: 'Cookies and Tracking' },
    { id: 'user-rights', label: 'Your Rights' },
    { id: 'data-retention', label: 'Data Retention' },
    { id: 'third-party', label: 'Third-Party Services' },
    { id: 'children-privacy', label: 'Children\'s Privacy' },
    { id: 'policy-changes', label: 'Changes to Privacy Policy' },
    { id: 'contact-us', label: 'Contact Us' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>      
      {/* Fixed Sidebar */}
      <InPageNavSidebar sections={sections} offsetTop={96} />
      
      <Box sx={{ pt: 12, pb: 8, bgcolor: 'background.default' }}>
        <Container 
          maxWidth="lg" 
          sx={{ 
            ml: { xs: 0, lg: '320px' },
            px: { xs: 2, sm: 3, md: 4 },
            maxWidth: { xs: '100%', lg: 'calc(100% - 320px)' },
            width: { xs: '100%', lg: 'calc(100% - 320px)' }
          }}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <Typography
                variant="h3"
                fontWeight="bold"
                gutterBottom
                sx={{
                  textAlign: 'center',
                  mb: 4,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  backgroundImage: 'var(--primary-gradient)',
                  backgroundClip: 'text',
                  color: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                textAlign="center"
                sx={{ mb: 6 }}
              >
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            </motion.div>

            <Paper
              elevation={2}
              sx={{
                p: { xs: 3, md: 6 },
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }} id="introduction">
                  Introduction
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  AkMo Solutions Private Limited ("we," "our," or "us") operates the AkMo Books accounting software 
                  (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                  information when you use our Service.
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                  We are committed to protecting your privacy and ensuring the security of your personal and business 
                  information. By using our Service, you agree to the collection and use of information in accordance 
                  with this Privacy Policy.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }} id="information-collection">
                  Information We Collect
                </Typography>
                
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3, mb: 2 }}>
                  Personal Information
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Account Information"
                      secondary="Name, email address, phone number, business name, and contact details"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Business Information"
                      secondary="GST number, business address, bank account details, and financial records"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Client Data"
                      secondary="Customer information, invoice details, and transaction history"
                    />
                  </ListItem>
                </List>

                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3, mb: 2 }}>
                  Technical Information
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Usage Data"
                      secondary="How you interact with our Service, features used, and time spent"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Device Information"
                      secondary="IP address, browser type, operating system, and device identifiers"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Cookies and Tracking"
                      secondary="Cookies, web beacons, and similar technologies to enhance your experience"
                    />
                  </ListItem>
                </List>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }} id="information-use">
                  How We Use Your Information
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Service Provision"
                      secondary="To provide, maintain, and improve our accounting software and related services"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Customer Support"
                      secondary="To respond to your inquiries, provide technical support, and resolve issues"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Communication"
                      secondary="To send you important updates, notifications, and marketing communications"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Security"
                      secondary="To protect against fraud, unauthorized access, and other security threats"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Legal Compliance"
                      secondary="To comply with applicable laws, regulations, and legal obligations"
                    />
                  </ListItem>
                </List>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }} id="information-sharing">
                  Information Sharing and Disclosure
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                  We do not sell, trade, or rent your personal information to third parties. We may share your 
                  information only in the following circumstances:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Service Providers"
                      secondary="With trusted third-party vendors who assist us in operating our Service"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Legal Requirements"
                      secondary="When required by law or to protect our rights, property, or safety"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Business Transfers"
                      secondary="In connection with a merger, acquisition, or sale of assets"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Consent"
                      secondary="When you have given us explicit consent to share your information"
                    />
                  </ListItem>
                </List>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }} id="data-security">
                  Data Security
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                  We implement appropriate technical and organizational measures to protect your information:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Encryption"
                      secondary="Data is encrypted in transit and at rest using industry-standard protocols"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Access Controls"
                      secondary="Strict access controls and authentication mechanisms to prevent unauthorized access"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Regular Audits"
                      secondary="Regular security audits and assessments to identify and address vulnerabilities"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Employee Training"
                      secondary="Comprehensive training for employees on data protection and privacy practices"
                    />
                  </ListItem>
                </List>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }} id="user-rights">
                  Your Rights and Choices
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                  You have the following rights regarding your personal information:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Access"
                      secondary="Request access to your personal information we hold"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Correction"
                      secondary="Request correction of inaccurate or incomplete information"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Deletion"
                      secondary="Request deletion of your personal information"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Portability"
                      secondary="Request a copy of your data in a structured, machine-readable format"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Opt-out"
                      secondary="Opt-out of marketing communications and certain data processing activities"
                    />
                  </ListItem>
                </List>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }} id="data-retention">
                  Data Retention
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                  We retain your personal information for as long as necessary to provide our Service and fulfill 
                  the purposes outlined in this Privacy Policy. We will delete or anonymize your information when:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Account Closure"
                      secondary="You close your account and request data deletion"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Legal Requirements"
                      secondary="Retention period required by applicable law has expired"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Business Purposes"
                      secondary="No longer necessary for legitimate business purposes"
                    />
                  </ListItem>
                </List>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }} id="third-party">
                  International Data Transfers
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                  Your information may be transferred to and processed in countries other than your own. We ensure 
                  that such transfers comply with applicable data protection laws and implement appropriate safeguards 
                  to protect your information.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }} id="children-privacy">
                  Children's Privacy
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                  Our Service is not intended for children under 13 years of age. We do not knowingly collect 
                  personal information from children under 13. If you are a parent or guardian and believe your 
                  child has provided us with personal information, please contact us.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }} id="policy-changes">
                  Changes to This Privacy Policy
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                  the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to 
                  review this Privacy Policy periodically for any changes.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }} id="contact-us">
                  Contact Us
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                  If you have any questions about this Privacy Policy or our privacy practices, please contact us:
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body1" paragraph>
                    <strong>Email:</strong> privacy@akmobooks.com
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Phone:</strong> +91-6363154142
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Address:</strong><br />
                    AkMo Solutions Private Limited<br />
                    No.14, SP, Sidco T.V.K Industrial Estate<br />
                    Guindy, Chennai, Tamilnadu, 600032<br />
                    India
                  </Typography>
                </Box>
              </motion.div>

              <Divider sx={{ my: 4 }} />
              
              <motion.div variants={itemVariants}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  This Privacy Policy is effective as of {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} 
                  and will remain in effect except with respect to any changes in its provisions in the future.
                </Typography>
              </motion.div>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Footer */}    </Box>
  );
};

export default PrivacyPolicy;
